import angular from 'angular';
import m from './module';
import Language from '../model/Language';
import { createError, isString } from '../util';

const type = 'Language';
const languagePropertySuffix = '_l10n';
const localLanguageList = require('../../../_buildconfig/translations/languages.json');

/**
 * TODO: Make these dynamic somehow
 */
const momentLocales = {
  'en_US': { locale: 'en', load: false },
  'fr_CA': { locale: 'fr-ca', path: 'moment/locale/fr-ca' },
};
const angularLocales = {
  'en_US': { locale: 'en-us', path: 'angular-i18n/en-us' },
  'fr_CA': { locale: 'fr-ca', path: 'angular-i18n/fr-ca' },
};

class LanguageService {
  constructor(
    $q,
    $rootScope,
    $translate,
    $injector,
    $sce,
    amMoment,
    tmhDynamicLocale,
    tmhDynamicLocaleCache,
    DEFAULT_LOCALE,
    DataSyncService,
    LogService
  ) {
    "ngInject";
    this.$q = $q;
    this.$rootScope = $rootScope;
    this.$translate = $translate;
    this.$injector = $injector;
    this.$sce = $sce;
    this.amMoment = amMoment;
    this.tmhDynamicLocale = tmhDynamicLocale;
    this.tmhDynamicLocaleCache = tmhDynamicLocaleCache;
    this.DEFAULT_LOCALE = DEFAULT_LOCALE;
    this.DataSyncService = DataSyncService;
    this.LogService = LogService;
    this._languages = [];
    this._systemDefaultLanguage = null;
    this._initPromise = null;
    this._initDefer = $q.defer();

    this._initAngularLocales();
    this._initMomentLocales();
  }

  set languages(languages) {
    this._languages = languages;
  }

  get languages() {
    return this._languages;
  }

  get languagesPromise() {
    return this.initCompletedPromise
      .then(() => this.languages);
  }

  get initCompletedPromise() {
    return this._initDefer.promise;
  }

  get activeLanguage() {
    return this._activeLanguage;
  }

  get systemDefaultLanguage() {
    return this._systemDefaultLanguage;
  }

  setActiveLanguage(language) {
    this._activeLanguage = language;
    const locale = language.locale;

    this.LogService.debug(`LanguageService.setActiveLanguage() setting active language to`, language);

    let promises = [];

    promises.push(this.$q.when(this._setAngularLocale(locale)));
    promises.push(this.$q.when(this._setAngularTranslateLocale(locale)));
    promises.push(this.$q.when(this._setMomentLocale(locale)));

    return this.$q.all(promises)
      .then(() => {
        this.$rootScope.$broadcast('LanguageServiceChangedLanguage', language);
        return language;
      });
  }

  _getInjectedLocale() {
    const localInjector = angular.injector(['ngLocale']);
    return localInjector.get('$locale');
  }

  _initAngularLocales() {
    // TODO: Find a way to do this dynamically

    require('angular-i18n/en-us');
    const localeEnUs = this._getInjectedLocale();
    this.tmhDynamicLocaleCache.put('en-us', localeEnUs);
    this.LogService.debug(`LanguageService._initAngularLocales() added en-us:`, localeEnUs);

    require('angular-i18n/fr-ca');
    const localeFrCa = this._getInjectedLocale();
    this.tmhDynamicLocaleCache.put('fr-ca', localeFrCa);
    this.LogService.debug(`LanguageService._initAngularLocales() added fr-ca:`, localeFrCa);
  }

  _initMomentLocales() {
    require('moment/locale/fr-ca');
  }

  _setAngularLocale(locale) {
    const localeInfo = angularLocales[locale];
    if (localeInfo) {
      const angularLocale = localeInfo.locale;

      this.tmhDynamicLocale.set(angularLocale);
      this.LogService.debug(`Set angular locale to ${angularLocale}`);
    } else {
      this.LogService.warn(`LanguageService._setAngularLocale() Couldn't find angular locale for ${locale}`);
    }
  }

  _setAngularTranslateLocale(locale) {
    // Set active language for angular-translate ($translate), used throughout site
    this.LogService.debug(`Setting angular-translate locale to ${locale}`);
    return this.$translate.use(locale);
  }

  _setMomentLocale(locale) {
    const localeInfo = momentLocales[locale];
    if (localeInfo) {
      const momentLocale = localeInfo.locale;
      this.amMoment.changeLocale(momentLocale);
      this.LogService.debug(`LanguageService._setMomentLocale() set locale to ${momentLocale}`);
    } else {
      this.LogService.warn(`LanguageService._setMomentLocale() couldn't find moment locale for ${locale}`);
    }
  }

  setActiveLanguageWithLocale(locale) {
    const language = this.getLanguageByLocale(locale);
    if (language) {
      return this.setActiveLanguage(language);
    } else {
      this.LogService.warn(`LanguageService.setActiveLanguageWithLocale(${locale}) could not find language. My languages:`, this.languages);
      return this.$q.reject(createError({
        message: 'Unknown language'
      }));
    }
  }

  setActiveLanguageWithId(languageId) {
    const language = this.getLanguageById(languageId);
    return language && this.setActiveLanguageWithLocale(language.locale);
  }

  initialize() {
    if (!this._initPromise) {
      this._initPromise = this.createAll(localLanguageList)
        .then(languages => {
          this.LogService.debug(`LanguageService.initialize() got languages:`, languages, `Setting active to default locale:`, this.DEFAULT_LOCALE);
          this.setActiveLanguageWithLocale(this.DEFAULT_LOCALE);
          this._systemDefaultLanguage = languages.find(language => language.isSystemDefault);
          this.LogService.debug(`LanguageService.initialize() set system default language to`, this._systemDefaultLanguage);
          this._initDefer.resolve(languages);
          return languages;
        });
    }
    return this._initPromise;
  }

  createAll(languageDatas) {
    const languagePromises = languageDatas.map(data => this.create(data));
    return this.$q.all(languagePromises)
      .then(languages => {
        return languages;
      });
  }

  create(data) {
    data = data || {};

    let language = new Language(data);
    return this.$q.when(language)
      .then(language => {
        this.languages.push(language);
        return language;
      });
  }

  getLanguageByLocale(locale) {
    return this.languages.find(language => language.locale === locale);
  }

  getLanguageById(id) {
    return this.languages.find(language => language.id === id);
  }

  translateProperty(target, propertyName) {
    if (!target) {
      this.LogService.warn(`LanguageService.translateProperty() called without target`);
      return null;
    }

    const propertyNameWithLanguageSuffix = `${propertyName}${languagePropertySuffix}`;
    const defaultValue = target[propertyName];

    if (target[propertyNameWithLanguageSuffix] === undefined) {
      return defaultValue;
    }

    const defaultLanguageLocale = this.DEFAULT_LOCALE;
    const activeLanguageLocale = this.activeLanguage.locale;
    let tryLanguages = [];
    if (activeLanguageLocale) {
      tryLanguages.push(activeLanguageLocale);
    }
    if (tryLanguages.indexOf(defaultLanguageLocale) < 0) {
      tryLanguages.push(defaultLanguageLocale);
    }

    for (const currLanguage of tryLanguages) {
      if (target[propertyNameWithLanguageSuffix][currLanguage]) {
        return target[propertyNameWithLanguageSuffix][currLanguage];
      }
    }

    return defaultValue;
  }

  populateMultiLanguageFields(data, multiLanguageFields) {
    let augmentedData = {...data};

    multiLanguageFields.forEach(field => {
      const languageFieldName = `${field}${languagePropertySuffix}`;

      if (augmentedData[languageFieldName] === undefined) {
        augmentedData[languageFieldName] = {};
      }

      if (augmentedData[field] === undefined) {
        augmentedData[field] = null;
      } else {
        augmentedData[field] = this.unwrapValue(augmentedData[field]);
      }

      this.languages.forEach(language => {

        // Ensure that language key exists
        if (augmentedData[languageFieldName][language.locale] === undefined) {
          augmentedData[languageFieldName][language.locale] = null;
        }

        if (augmentedData[languageFieldName][language.locale]) {
          // Ensure value is unwrapped
          const unwrapped = this.unwrapValue(augmentedData[languageFieldName][language.locale]);;
          augmentedData[languageFieldName][language.locale] = unwrapped;
        }

        if (language.isSystemDefault) {
          // Populate from single-language field if system default field is empty
          if (augmentedData[languageFieldName][language.locale]) {
            // If multi-language value is present for system default language, overwrite single-language value
            augmentedData[field] = this.unwrapValue(augmentedData[languageFieldName][language.locale]);
          } else {
            // If multi-language value is not present for system default language, populate from single-language value
            augmentedData[languageFieldName][language.locale] = this.unwrapValue(augmentedData[field]);
          }
        }
      });
    });

    return augmentedData;
  }

  unwrapValue(stringOrObject) {
    if (!stringOrObject || isString(stringOrObject)) {
      return stringOrObject;
    }
    return this.$sce.valueOf(stringOrObject);
  }
}

export default LanguageService;

m.service('LanguageService', LanguageService);

m.filter('translateProperty', (LanguageService) => {
  "use strict";
  return (target, propertyName) => {
    return LanguageService.translateProperty(target, propertyName);
  };
});
